import { Alert, Button, Card, Drawer, Form, Input, InputNumber, Select, Space, Switch, Table, Tabs, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createAdminProduct,
  createAdminProductionLot,
  createAdminProductUnit,
  listAdminIncidentForms,
  listAdminProducts,
  listAdminProductionLots,
  listAdminProductUnits,
  listFactories,
  updateAdminProduct,
  updateAdminProductionLot,
  updateAdminProductUnit,
} from '../services/api';
import type {
  CurrentUser,
  IncidentFormDefinition,
  Factory,
  ProductMaster,
  ProductMasterUpsert,
  ProductionLotMaster,
  ProductionLotMasterUpsert,
  ProductUnitMaster,
  ProductUnitMasterUpsert,
} from '../types/incident';

const { Text } = Typography;

type ProductTabKey = 'products' | 'lots' | 'units';
type EditingRecord = ProductMaster | ProductionLotMaster | ProductUnitMaster;
type ProductFormValues = ProductMasterUpsert & ProductionLotMasterUpsert & ProductUnitMasterUpsert;

type AdminProductMasterPageProps = {
  currentUser: CurrentUser | null;
};

const cleanString = (value?: string | null): string | null => {
  if (value === undefined || value === null) return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const toProductPayload = (values: ProductFormValues): ProductMasterUpsert => ({
  product_name: values.product_name.trim(),
  model_number: values.model_number.trim(),
  is_active: values.is_active ?? true,
  incident_form_id: values.incident_form_id || null,
});

const toLotPayload = (values: ProductFormValues): ProductionLotMasterUpsert => ({
  product_id: values.product_id,
  factory_id: values.factory_id,
  lot_number: values.lot_number.trim(),
  production_date: cleanString(values.production_date),
  quantity: values.quantity ?? null,
});

const toUnitPayload = (values: ProductFormValues): ProductUnitMasterUpsert => ({
  product_id: values.product_id,
  production_lot_id: values.production_lot_id || null,
  serial_no: values.serial_no.trim(),
});

const AdminProductMasterPage = ({ currentUser }: AdminProductMasterPageProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<ProductFormValues>();
  const [messageApi, contextHolder] = message.useMessage();
  const [activeTab, setActiveTab] = useState<ProductTabKey>('products');
  const [products, setProducts] = useState<ProductMaster[]>([]);
  const [lots, setLots] = useState<ProductionLotMaster[]>([]);
  const [units, setUnits] = useState<ProductUnitMaster[]>([]);
  const [factories, setFactories] = useState<Factory[]>([]);
  const [incidentForms, setIncidentForms] = useState<IncidentFormDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<EditingRecord | null>(null);
  const [keyword, setKeyword] = useState('');

  const isSystemAdmin = useMemo(
    () => currentUser?.assignments.some((assignment) => assignment.role === 'SYSTEM_ADMIN' && assignment.is_active) || false,
    [currentUser?.assignments]
  );

  const loadMasters = useCallback(async () => {
    setLoading(true);
    try {
      const [nextProducts, nextLots, nextUnits, nextFactories, nextIncidentForms] = await Promise.all([
        listAdminProducts(),
        listAdminProductionLots(),
        listAdminProductUnits(),
        listFactories(),
        listAdminIncidentForms(),
      ]);
      setProducts(nextProducts);
      setLots(nextLots);
      setUnits(nextUnits);
      setFactories(nextFactories);
      setIncidentForms(nextIncidentForms);
    } catch {
      messageApi.error(t('adminProductMaster.messages.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [messageApi, t]);

  useEffect(() => {
    if (isSystemAdmin) {
      loadMasters();
    } else {
      setLoading(false);
    }
  }, [isSystemAdmin, loadMasters]);

  const productOptions = useMemo(
    () =>
      products.map((product) => ({
        value: product.id,
        label: `${product.product_name} / ${product.model_number}`,
      })),
    [products]
  );

  const factoryOptions = useMemo(
    () =>
      factories.map((factory) => ({
        value: factory.id,
        label: `${factory.name} (${factory.code})`,
      })),
    [factories]
  );

  const lotOptions = useMemo(
    () =>
      lots.map((lot) => ({
        value: lot.id,
        label: `${lot.product_name} / ${lot.model_number} / ${lot.lot_number} / ${lot.factory_name}`,
      })),
    [lots]
  );

  const incidentFormOptions = useMemo(
    () =>
      incidentForms
        .filter((item) => item.is_active)
        .map((item) => ({
          value: item.id,
          label: item.name,
        })),
    [incidentForms]
  );

  const normalizedKeyword = keyword.trim().toLowerCase();
  const filteredProducts = useMemo(() => {
    if (!normalizedKeyword) return products;
    return products.filter((product) =>
      `${product.product_name} ${product.model_number}`.toLowerCase().includes(normalizedKeyword)
    );
  }, [normalizedKeyword, products]);

  const filteredLots = useMemo(() => {
    if (!normalizedKeyword) return lots;
    return lots.filter((lot) =>
      `${lot.product_name} ${lot.model_number} ${lot.lot_number} ${lot.factory_name} ${lot.factory_code}`.toLowerCase().includes(normalizedKeyword)
    );
  }, [lots, normalizedKeyword]);

  const filteredUnits = useMemo(() => {
    if (!normalizedKeyword) return units;
    return units.filter((unit) =>
      `${unit.product_name} ${unit.model_number} ${unit.serial_no} ${unit.lot_number || ''} ${unit.factory_name || ''}`.toLowerCase().includes(normalizedKeyword)
    );
  }, [normalizedKeyword, units]);

  const openCreateDrawer = (tab: ProductTabKey = activeTab) => {
    setActiveTab(tab);
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true } as Partial<ProductFormValues>);
    setDrawerOpen(true);
  };

  const openEditDrawer = (tab: ProductTabKey, record: EditingRecord) => {
    setActiveTab(tab);
    setEditingRecord(record);
    form.resetFields();
    form.setFieldsValue(record as Partial<ProductFormValues>);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingRecord(null);
    form.resetFields();
  };

  const handleSubmit = async (values: ProductFormValues) => {
    setSaving(true);
    try {
      if (activeTab === 'products') {
        const saved = editingRecord
          ? await updateAdminProduct(editingRecord.id, toProductPayload(values))
          : await createAdminProduct(toProductPayload(values));
        setProducts((current) => [saved, ...current.filter((item) => item.id !== saved.id)].sort((a, b) => a.product_name.localeCompare(b.product_name)));
      } else if (activeTab === 'lots') {
        const saved = editingRecord
          ? await updateAdminProductionLot(editingRecord.id, toLotPayload(values))
          : await createAdminProductionLot(toLotPayload(values));
        setLots((current) => [saved, ...current.filter((item) => item.id !== saved.id)]);
      } else {
        const saved = editingRecord
          ? await updateAdminProductUnit(editingRecord.id, toUnitPayload(values))
          : await createAdminProductUnit(toUnitPayload(values));
        setUnits((current) => [saved, ...current.filter((item) => item.id !== saved.id)]);
      }
      messageApi.success(editingRecord ? t('adminProductMaster.messages.updated') : t('adminProductMaster.messages.created'));
      closeDrawer();
      await loadMasters();
    } catch {
      messageApi.error(t('adminProductMaster.messages.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const productColumns: ColumnsType<ProductMaster> = [
    {
      title: t('adminProductMaster.columns.product'),
      key: 'product',
      width: 260,
      render: (_value, product) => (
        <Space direction="vertical" size={2}>
          <Text strong>{product.product_name}</Text>
          <Text type="secondary">{product.model_number}</Text>
        </Space>
      ),
    },
    {
      title: t('adminProductMaster.columns.status'),
      dataIndex: 'is_active',
      width: 110,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'default'}>
          {isActive ? t('adminProductMaster.status.active') : t('adminProductMaster.status.inactive')}
        </Tag>
      ),
    },
    { title: t('adminProductMaster.columns.lots'), dataIndex: 'lot_count', width: 90 },
    { title: t('adminProductMaster.columns.units'), dataIndex: 'unit_count', width: 90 },
    {
      title: t('adminProductMaster.columns.incidentForm'),
      dataIndex: 'incident_form_name',
      width: 180,
      render: (value?: string | null) => value || '-',
    },
    { title: t('master.stats.incidents'), dataIndex: 'incident_count', width: 120 },
    {
      title: t('adminProductMaster.columns.actions'),
      key: 'actions',
      width: 100,
      render: (_value, product) => (
        <Button size="small" onClick={() => openEditDrawer('products', product)}>
          {t('adminProductMaster.actions.edit')}
        </Button>
      ),
    },
  ];

  const lotColumns: ColumnsType<ProductionLotMaster> = [
    {
      title: t('adminProductMaster.columns.product'),
      key: 'product',
      width: 260,
      render: (_value, lot) => (
        <Space direction="vertical" size={2}>
          <Text strong>{lot.product_name}</Text>
          <Text type="secondary">{lot.model_number}</Text>
        </Space>
      ),
    },
    { title: t('adminProductMaster.columns.lotNumber'), dataIndex: 'lot_number', width: 150 },
    { title: t('adminProductMaster.columns.factory'), dataIndex: 'factory_name', ellipsis: true },
    { title: t('adminProductMaster.columns.productionDate'), dataIndex: 'production_date', width: 140, render: (value?: string | null) => value || '-' },
    { title: t('adminProductMaster.columns.quantity'), dataIndex: 'quantity', width: 100, render: (value?: number | null) => value ?? '-' },
    { title: t('adminProductMaster.columns.units'), dataIndex: 'unit_count', width: 90 },
    {
      title: t('adminProductMaster.columns.actions'),
      key: 'actions',
      width: 100,
      render: (_value, lot) => (
        <Button size="small" onClick={() => openEditDrawer('lots', lot)}>
          {t('adminProductMaster.actions.edit')}
        </Button>
      ),
    },
  ];

  const unitColumns: ColumnsType<ProductUnitMaster> = [
    {
      title: t('adminProductMaster.columns.serialNo'),
      dataIndex: 'serial_no',
      width: 180,
      render: (value: string) => <Text strong>{value}</Text>,
    },
    {
      title: t('adminProductMaster.columns.product'),
      key: 'product',
      width: 260,
      render: (_value, unit) => (
        <Space direction="vertical" size={2}>
          <Text>{unit.product_name}</Text>
          <Text type="secondary">{unit.model_number}</Text>
        </Space>
      ),
    },
    { title: t('adminProductMaster.columns.lotNumber'), dataIndex: 'lot_number', width: 150, render: (value?: string | null) => value || '-' },
    { title: t('adminProductMaster.columns.factory'), dataIndex: 'factory_name', ellipsis: true, render: (value?: string | null) => value || '-' },
    { title: t('master.stats.incidents'), dataIndex: 'incident_count', width: 120 },
    {
      title: t('adminProductMaster.columns.actions'),
      key: 'actions',
      width: 100,
      render: (_value, unit) => (
        <Button size="small" onClick={() => openEditDrawer('units', unit)}>
          {t('adminProductMaster.actions.edit')}
        </Button>
      ),
    },
  ];

  if (!isSystemAdmin) {
    return (
      <div className="page">
        {contextHolder}
        <Alert type="warning" showIcon message={t('adminProductMaster.forbidden')} />
      </div>
    );
  }

  return (
    <div className="page adminMasterPage">
      {contextHolder}
      <div className="pageHeader">
        <p>{t('adminProductMaster.description')}</p>
      </div>

      <Card
        className="adminMasterCard"
        extra={
          <Button type="primary" onClick={() => openCreateDrawer()}>
            {t(`adminProductMaster.actions.create.${activeTab}`)}
          </Button>
        }
      >
        <Input.Search
          className="adminMasterSearch"
          allowClear
          placeholder={t('master.filters.keyword')}
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
        />
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as ProductTabKey)}
          items={[
            {
              key: 'products',
              label: t('adminProductMaster.tabs.products'),
              children: (
                <Table
                  rowKey="id"
                  columns={productColumns}
                  dataSource={filteredProducts}
                  loading={loading}
                  pagination={{ pageSize: 20 }}
                />
              ),
            },
            {
              key: 'lots',
              label: t('adminProductMaster.tabs.lots'),
              children: (
                <Table
                  rowKey="id"
                  columns={lotColumns}
                  dataSource={filteredLots}
                  loading={loading}
                  pagination={{ pageSize: 20 }}
                  scroll={{ x: 980 }}
                />
              ),
            },
            {
              key: 'units',
              label: t('adminProductMaster.tabs.units'),
              children: (
                <Table
                  rowKey="id"
                  columns={unitColumns}
                  dataSource={filteredUnits}
                  loading={loading}
                  pagination={{ pageSize: 20 }}
                  scroll={{ x: 980 }}
                />
              ),
            },
          ]}
        />
      </Card>

      <Drawer
        title={editingRecord ? t('adminProductMaster.form.editTitle') : t('adminProductMaster.form.createTitle')}
        open={drawerOpen}
        onClose={closeDrawer}
        width={560}
        destroyOnClose
        extra={
          <Space>
            <Button onClick={closeDrawer}>{t('common.cancel')}</Button>
            <Button type="primary" loading={saving} onClick={() => form.submit()}>
              {editingRecord ? t('adminProductMaster.actions.update') : t('adminProductMaster.actions.save')}
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {activeTab === 'products' ? (
            <>
              <Form.Item name="product_name" label={t('adminProductMaster.columns.productName')} rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="model_number" label={t('adminProductMaster.columns.modelNumber')} rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="is_active" label={t('adminProductMaster.columns.status')} valuePropName="checked">
                <Switch />
              </Form.Item>
              <Form.Item name="incident_form_id" label={t('adminProductMaster.columns.incidentForm')}>
                <Select allowClear showSearch optionFilterProp="label" options={incidentFormOptions} />
              </Form.Item>
            </>
          ) : null}

          {activeTab === 'lots' ? (
            <>
              <Form.Item name="product_id" label={t('adminProductMaster.columns.product')} rules={[{ required: true }]}>
                <Select showSearch optionFilterProp="label" options={productOptions} />
              </Form.Item>
              <Form.Item name="factory_id" label={t('adminProductMaster.columns.factory')} rules={[{ required: true }]}>
                <Select showSearch optionFilterProp="label" options={factoryOptions} />
              </Form.Item>
              <Form.Item name="lot_number" label={t('adminProductMaster.columns.lotNumber')} rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="production_date" label={t('adminProductMaster.columns.productionDate')}>
                <Input placeholder="YYYY-MM-DD" />
              </Form.Item>
              <Form.Item name="quantity" label={t('adminProductMaster.columns.quantity')}>
                <InputNumber min={0} precision={0} />
              </Form.Item>
            </>
          ) : null}

          {activeTab === 'units' ? (
            <>
              <Form.Item name="product_id" label={t('adminProductMaster.columns.product')} rules={[{ required: true }]}>
                <Select showSearch optionFilterProp="label" options={productOptions} />
              </Form.Item>
              <Form.Item name="production_lot_id" label={t('adminProductMaster.columns.productionLot')}>
                <Select allowClear showSearch optionFilterProp="label" options={lotOptions} />
              </Form.Item>
              <Form.Item name="serial_no" label={t('adminProductMaster.columns.serialNo')} rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </>
          ) : null}
        </Form>
      </Drawer>
    </div>
  );
};

export default AdminProductMasterPage;
